import React, { useMemo } from 'react';
import { useSvgAsset, useRecoloredUrl } from './useSvg';
import { resolveColors, parsePaths } from './svgColors';
import { NUMBER_CONFIG, NAME_CONFIG } from './textConfigs';

// Every asset is authored on this artboard.
export const W = 1650;
export const H = 1550;

const FULL = { x: 0, y: 0, width: W, height: H, preserveAspectRatio: 'none' };
// Everything on the garment body is clipped to the paths in clipmask-<garment>.svg,
// used exactly as drawn (no growing, no extra masking).
const CLIP = 'url(#garment-clip)';

// Pattern that fills the garment body, recoloured with the user's choices.
function BodyPattern({ pattern, chosen }) {
    const asset = useSvgAsset(pattern ? pattern.url : null);
    const url = useRecoloredUrl(asset, asset ? resolveColors(asset.slots, chosen) : null);
    return url ? <image href={url} {...FULL} clipPath={CLIP} /> : null;
}

// One stripe: either filled with its own colour(s) or used as a mask for a pattern.
function StripeLayer({ stripe, config, patterns, bodyColors, stripeDefaults }) {
    const pattern = config.patternId
        ? patterns.find((p) => p.id === config.patternId) || null
        : null;

    const stripeAsset = useSvgAsset(stripe.url);
    const patternAsset = useSvgAsset(pattern ? pattern.url : null);

    const solidUrl = useRecoloredUrl(
        pattern ? null : stripeAsset,
        stripeAsset ? resolveColors(stripeAsset.slots, config.colors, stripeDefaults) : null
    );
    const maskUrl = useRecoloredUrl(pattern ? stripeAsset : null, null, true);
    const patternUrl = useRecoloredUrl(
        patternAsset,
        patternAsset
            ? resolveColors(patternAsset.slots, (config.patternColors || {})[pattern.id], bodyColors)
            : null
    );

    if (!pattern) {
        return solidUrl ? <image href={solidUrl} {...FULL} clipPath={CLIP} /> : null;
    }
    if (!maskUrl || !patternUrl) return null;

    const maskId = `stripe-mask-${stripe.id}`;
    return (
        <g clipPath={CLIP}>
            <defs>
                <mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse"
                    x="0" y="0" width={W} height={H}>
                    <image href={maskUrl} {...FULL} />
                </mask>
            </defs>
            <image href={patternUrl} {...FULL} mask={`url(#${maskId})`} />
        </g>
    );
}

// Uploaded logo, centred on (x, y).
const LOGO_SIZE = 200;
function LogoLayer({ logo }) {
    return (
        <g clipPath={CLIP}>
            <g transform={`translate(${logo.x},${logo.y}) rotate(${logo.rotate}) scale(${logo.scale})`}>
                <image
                    href={logo.dataUrl}
                    x={-LOGO_SIZE / 2}
                    y={-LOGO_SIZE / 2}
                    width={LOGO_SIZE}
                    height={LOGO_SIZE}
                    preserveAspectRatio="xMidYMid meet"
                />
            </g>
        </g>
    );
}

// Outlined text (jersey number or name), centred on (x, y).
function TextLayer({ item, config }) {
    return (
        <g clipPath={CLIP}>
            <text
                x={item.x}
                y={item.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontFamily={config.font}
                fontSize={config.baseFontSize * item.scale}
                fontWeight={config.fontWeight}
                letterSpacing={config.letterSpacing}
                transform={`rotate(${item.rotate}, ${item.x}, ${item.y})`}
                stroke={item.outlineColor}
                strokeWidth={item.outlineWidth}
                strokeLinejoin="miter"
                strokeMiterlimit="10"
                fill={item.fillColor}
                paintOrder="stroke"
                style={{ userSelect: 'none' }}
            >
                {item[config.valueKey] || (config === NUMBER_CONFIG ? '0' : '')}
            </text>
        </g>
    );
}

/**
 * GarmentPreview – composites the garment on the 1650x1550 artboard.
 *
 * Layers, bottom to top:
 *   1 background PNG   2 body pattern (clipped)   3 stripes (clipped)
 *   4 logos   5 numbers   6 names (all clipped)
 *   7 multiply PNG     8 outline SVG
 *
 * Props:
 *   garment        – entry from assetManifest GARMENTS
 *   patterns       – PATTERNS
 *   patternId      – body pattern id
 *   bodyColors     – string[]  body colour per slot, shared by every pattern
 *   stripeDefaults – string[]  default colour per slot for solid stripes
 *   stripeIds      – enabled stripe ids, in stacking order
 *   stripeConfigs  – { [stripeId]: { patternId, colors, patternColors } }
 *   logos, numbers, names – placed items (see LogoPanel / textConfigs)
 *   svgRef         – ref to the root <svg> (used by the PNG export)
 */
export default function GarmentPreview({
    garment, patterns, patternId, bodyColors, stripeDefaults, stripeIds, stripeConfigs,
    logos = [], numbers = [], names = [], svgRef,
}) {
    const clipAsset = useSvgAsset(garment.clipmask);
    const clipPaths = useMemo(() => (clipAsset ? parsePaths(clipAsset.text) : []), [clipAsset]);
    const bodyPattern = patterns.find((p) => p.id === patternId) || null;

    return (
        <svg
            ref={svgRef}
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${W} ${H}`}
            style={{
                width: '100%',
                maxWidth: '900px',
                maxHeight: 'calc(100vh - 130px)',
                display: 'block',
                margin: '0 auto',
            }}
            aria-label="Garment preview"
        >
            <defs>
                <clipPath id="garment-clip" clipPathUnits="userSpaceOnUse">
                    {clipPaths.map((d, i) => <path key={i} d={d} />)}
                </clipPath>
            </defs>

            <image href={garment.background} {...FULL} />

            <BodyPattern pattern={bodyPattern} chosen={bodyColors} />

            {stripeIds.map((id) => {
                const stripe = garment.stripes.find((s) => s.id === id);
                return stripe ? (
                    <StripeLayer
                        key={id}
                        stripe={stripe}
                        config={stripeConfigs[id] || {}}
                        patterns={patterns}
                        bodyColors={bodyColors}
                        stripeDefaults={stripeDefaults}
                    />
                ) : null;
            })}

            {logos.map((logo) => <LogoLayer key={logo.id} logo={logo} />)}
            {numbers.map((n) => <TextLayer key={n.id} item={n} config={NUMBER_CONFIG} />)}
            {names.map((n) => <TextLayer key={n.id} item={n} config={NAME_CONFIG} />)}

            {garment.multiply && (
                <image
                    href={garment.multiply}
                    {...FULL}
                    clipPath={CLIP}
                    style={{ mixBlendMode: 'multiply' }}
                />
            )}

            {garment.outline && <image href={garment.outline} {...FULL} />}
        </svg>
    );
}
